import { createApi } from '@reduxjs/toolkit/query/react';

import { apiBaseUrl } from '../Api';
import { makeBaseQuery } from '../baseQuery';
import type {
  AdminActivityRow,
  AdminBuyerDetail,
  AdminBuyerEnquiryDetail,
  AdminBuyerEnquiryRow,
  AdminBuyerListItem,
  AdminBuyerQueryArgs,
  AdminDealDetail,
  AdminDealRow,
  AdminEnquiryDetail,
  AdminEnquiryRow,
  AdminLawyerDetail,
  AdminLawyerQueryArgs,
  AdminLawyerRow,
  AdminLegalRequestQueryArgs,
  AdminLegalRequestRow,
  AdminNotificationCounts,
  AdminPropertyCounts,
  AdminPropertyCountsQueryArgs,
  AdminPropertyDetail,
  AdminPropertyListItem,
  AdminPropertyManagerQueryArgs,
  AdminPropertyManagerRow,
  AdminPropertyQueryArgs,
  AdminSellerDetail,
  AdminSellerListItem,
  AdminSellerQueryArgs,
  AdminStats,
  ApproveAdminEnquiryArgs,
  ApproveBuyerEnquiryArgs,
  AssignLawyerArgs,
  AssignManagerArgs,
  CreateLawyerArgs,
  CreateLawyerResult,
  CreatePropertyManagerArgs,
  DocumentVersionRow,
  IdResult,
  MessageResult,
  Paged,
  ReviewDocumentArgs,
  UpdateAdminPropertyStatusArgs,
  UpdateLegalRequestStatusArgs,
  UpdatePropertyManagerArgs
} from './adminTypes';

/**
 * The admin console: `/api/admin/*`, answered by six backend modules (admin, documents,
 * enquiries, buyer-enquiries, deals, notifications). One slice, because it is one prefix and
 * one audience — the split across the old `adminService`, `adminPropertyService`,
 * `adminSellerService`, `adminLawyerService`, `adminBuyerService` and `adminBrokerService`
 * followed nothing the API actually distinguishes.
 *
 * Ported endpoint-for-endpoint: url, method, params and body are unchanged.
 *
 * The normalizers are exported for the colocated test only. They are module-scope, take
 * `unknown`, and are TOTAL — never throwing, never `undefined`. The old services were
 * inconsistent about this: `adminBuyerService` threw `new Error('Failed')` on
 * `success === false` while `adminBrokerService` read `data.data` blind. Both behaviours are
 * absorbed here, so the failure path is now the query's own `error` state either way.
 */

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asRecord = (value: unknown): Record<string, unknown> => (isRecord(value) ? value : {});

const asRows = <T>(value: unknown): T[] =>
  (Array.isArray(value) ? value.filter(isRecord) : []) as unknown as T[];

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

const asOptionalNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

/** The one envelope: `{ success, message?, data }`. A reply with no `data` yields `{}`. */
const envelope = (response: unknown): unknown => asRecord(response).data;

// NOTE: the backend's maxLimit; the queue page paginates client-side over this.
const ADMIN_BUYER_ENQUIRY_QUEUE_LIMIT = 100;

/** A row that must exist to be useful: no numeric `id` means "not found", not a crash. */
const asEntity = <T>(value: unknown): T | null => {
  const record = asRecord(value);
  return asOptionalNumber(record['id']) === null ? null : (record as unknown as T);
};

/**
 * Both list envelopes in one shape. The admin lists answer `{ items, total, page, limit }`
 * with no `totalPages`; `paginated()` endpoints answer `{ items, pagination }` with one.
 * `totalPages` is DERIVED when absent, using the backend's own `Math.ceil(total / limit) || 1`.
 */
export function normalizePaged<T>(response: unknown): Paged<T> {
  const data = asRecord(envelope(response));
  const pagination = asRecord(data['pagination']);
  const items = asRows<T>(data['items']);
  const page = asNumber(pagination['page'] ?? data['page'], 1);
  const limit = asNumber(pagination['limit'] ?? data['limit'], 0);
  const total = asNumber(pagination['total'] ?? data['total'], items.length);
  const derived = limit > 0 ? Math.ceil(total / limit) || 1 : 1;
  return {
    items,
    page,
    limit,
    total,
    totalPages: asNumber(pagination['totalPages'] ?? data['totalPages'], derived)
  };
}

/** `data: [...]`. An object or a null `data` yields `[]`. */
export function normalizeRows<T>(response: unknown): T[] {
  return asRows<T>(envelope(response));
}

/** `data: { items: [...] }` with no paging at all — the buyer lists. */
export function normalizeItems<T>(response: unknown): T[] {
  return asRows<T>(asRecord(envelope(response))['items']);
}

export function normalizeMessage(response: unknown): MessageResult {
  return { message: asString(asRecord(response)['message']) };
}

export function normalizeIdResult(response: unknown): IdResult {
  const root = asRecord(response);
  return {
    id: asOptionalNumber(asRecord(root['data'])['id']),
    message: asString(root['message'])
  };
}

export function normalizeAdminStats(response: unknown): AdminStats {
  const data = asRecord(envelope(response));
  return {
    totalProperties: asNumber(data['totalProperties']),
    pendingReview: asNumber(data['pendingReview']),
    verifiedProperties: asNumber(data['verifiedProperties']),
    documentsNeedingAction: asNumber(data['documentsNeedingAction']),
    totalSellers: asNumber(data['totalSellers']),
    totalLawyers: asNumber(data['totalLawyers'])
  };
}

export function normalizeAdminNotificationCounts(response: unknown): AdminNotificationCounts {
  const data = asRecord(envelope(response));
  return {
    pendingProperties: asNumber(data['pendingProperties']),
    pendingDocuments: asNumber(data['pendingDocuments']),
    newSellerRegistrations: asNumber(data['newSellerRegistrations']),
    badgeCount: asNumber(data['badgeCount'])
  };
}

export function normalizeAdminPropertyCounts(response: unknown): AdminPropertyCounts {
  const data = asRecord(envelope(response));
  return {
    all: asNumber(data['all']),
    submitted: asNumber(data['submitted']),
    under_review: asNumber(data['under_review']),
    lawyer_verified: asNumber(data['lawyer_verified']),
    listed: asNumber(data['listed'])
  };
}

/** `data.districts` — the one endpoint that wraps a bare array in a named key. */
export function normalizeDistricts(response: unknown): string[] {
  const districts = asRecord(envelope(response))['districts'];
  return Array.isArray(districts)
    ? districts.filter((d): d is string => typeof d === 'string')
    : [];
}

/**
 * Thirteen branches, each independently optional. The seven trailing collections were typed
 * optional by the axios service; they always arrive, so they are guaranteed arrays here and
 * a caller never writes `?? []` again.
 */
export function normalizeAdminPropertyDetail(response: unknown): AdminPropertyDetail {
  const data = asRecord(envelope(response));
  return {
    property: asEntity<AdminPropertyDetail['property']>(data['property']),
    owners: asRows(data['owners']),
    documents: asRows(data['documents']),
    assignedLawyer: asEntity<AdminPropertyDetail['assignedLawyer']>(data['assignedLawyer']),
    timeline: asRows(data['timeline']),
    activity: asRows(data['activity']),
    lawyerDocumentReviews: asRows(data['lawyerDocumentReviews']),
    legalOpinion: asEntity<AdminPropertyDetail['legalOpinion']>(data['legalOpinion']),
    ownershipPersons: asRows(data['ownershipPersons']),
    ownershipRelationships: asRows(data['ownershipRelationships']),
    lawyerNotes: asRows(data['lawyerNotes']),
    lawyerSellerMessages: asRows(data['lawyerSellerMessages']),
    photos: asRows(data['photos']),
    videos: asRows(data['videos']),
    youtubeLinks: asRows(data['youtubeLinks'])
  };
}

export function normalizeAdminSellerDetail(response: unknown): AdminSellerDetail {
  const data = asRecord(envelope(response));
  return {
    user: asEntity<AdminSellerDetail['user']>(data['user']),
    properties: asRows(data['properties']),
    documentUploadHistory: asRows(data['documentUploadHistory'])
  };
}

export function normalizeAdminLawyerDetail(response: unknown): AdminLawyerDetail {
  const data = asRecord(envelope(response));
  return {
    user: asEntity<AdminLawyerDetail['user']>(data['user']),
    assignedProperties: asRows(data['assignedProperties']),
    documentsReviewedCount: asNumber(data['documentsReviewedCount'])
  };
}

export function normalizeAdminBuyerDetail(response: unknown): AdminBuyerDetail {
  const data = asRecord(envelope(response));
  return {
    user: asEntity<AdminBuyerDetail['user']>(data['user']),
    enquiries: asRows(data['enquiries']),
    negotiations: asRows(data['negotiations'])
  };
}

export function normalizeAdminBuyerEnquiryDetail(response: unknown): AdminBuyerEnquiryDetail {
  const data = asRecord(envelope(response));
  return {
    enquiry: asEntity<AdminBuyerEnquiryRow>(data['enquiry']),
    activity: asRows(data['activity'])
  };
}

/**
 * `SELECT e.*` plus six joins. No numeric `id` means the enquiry is gone — `null`, so the
 * screen shows its not-found branch instead of a page of blank labels.
 */
export function normalizeAdminEnquiry(response: unknown): AdminEnquiryDetail | null {
  return asEntity<AdminEnquiryDetail>(envelope(response));
}

export function normalizeCreateLawyerResult(response: unknown): CreateLawyerResult {
  const data = asRecord(envelope(response));
  return {
    id: asOptionalNumber(data['id']),
    name: asString(data['name']),
    email: asString(data['email']),
    mobile: asString(data['mobile'])
  };
}

export function normalizeAdminDealDetail(response: unknown): AdminDealDetail {
  const data = asRecord(envelope(response));
  return {
    deal: asEntity<AdminDealDetail['deal']>(data['deal']),
    notes: asRows(data['notes']),
    documents: asRows(data['documents']),
    stage_history: asRows(data['stage_history'])
  };
}

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: makeBaseQuery(apiBaseUrl),
  tagTypes: [
    'AdminProperty',
    'AdminSeller',
    'AdminLawyer',
    'AdminPropertyManager',
    'AdminBuyer',
    'LegalRequest',
    'PropertyDocument',
    'PropertyActivity',
    'Enquiry',
    'BuyerEnquiry',
    'Deal',
    'DealNote',
    'Notification',
    'DashboardStats'
  ],
  endpoints: (builder) => ({
    /* ───────────── overview ───────────── */

    getAdminStats: builder.query<AdminStats, void>({
      query: () => ({ url: 'admin/stats' }),
      transformResponse: (response: unknown) => normalizeAdminStats(response),
      providesTags: [{ type: 'DashboardStats', id: 'ADMIN' }]
    }),

    /** The twenty most recent activity-log entries across every property. */
    getAdminActivity: builder.query<AdminActivityRow[], void>({
      query: () => ({ url: 'admin/activity' }),
      transformResponse: (response: unknown) => normalizeRows<AdminActivityRow>(response),
      providesTags: [{ type: 'PropertyActivity', id: 'GLOBAL' }]
    }),

    getAdminNotifications: builder.query<AdminNotificationCounts, void>({
      query: () => ({ url: 'admin/notifications' }),
      transformResponse: (response: unknown) => normalizeAdminNotificationCounts(response),
      providesTags: [{ type: 'Notification', id: 'ADMIN' }]
    }),

    /* ───────────── properties ───────────── */

    getAdminProperties: builder.query<Paged<AdminPropertyListItem>, AdminPropertyQueryArgs>({
      query: (params) => ({ url: 'admin/properties', params: { ...params } }),
      transformResponse: (response: unknown) => normalizePaged<AdminPropertyListItem>(response),
      providesTags: (result) => [
        { type: 'AdminProperty' as const, id: 'LIST' },
        ...(result?.items ?? []).map((row) => ({
          type: 'AdminProperty' as const,
          id: String(row.id)
        }))
      ]
    }),

    getAdminPropertyCounts: builder.query<AdminPropertyCounts, AdminPropertyCountsQueryArgs>({
      query: (params) => ({ url: 'admin/properties/counts', params: { ...params } }),
      transformResponse: (response: unknown) => normalizeAdminPropertyCounts(response),
      providesTags: [{ type: 'AdminProperty', id: 'COUNTS' }]
    }),

    /** `/properties/meta/districts` is registered before `/properties/:id` on purpose. */
    getAdminDistricts: builder.query<string[], void>({
      query: () => ({ url: 'admin/properties/meta/districts' }),
      transformResponse: (response: unknown) => normalizeDistricts(response),
      providesTags: [{ type: 'AdminProperty', id: 'DISTRICTS' }]
    }),

    getAdminPropertyDetail: builder.query<AdminPropertyDetail, { propertyId: number }>({
      query: ({ propertyId }) => ({ url: `admin/properties/${propertyId}` }),
      transformResponse: (response: unknown) => normalizeAdminPropertyDetail(response),
      providesTags: (_result, _error, arg) => [
        { type: 'AdminProperty', id: String(arg.propertyId) },
        { type: 'PropertyDocument', id: String(arg.propertyId) }
      ]
    }),

    getAdminPropertyActivity: builder.query<
      Paged<AdminActivityRow>,
      { propertyId: number; page?: number; limit?: number }
    >({
      query: ({ propertyId, page, limit }) => ({
        url: `admin/properties/${propertyId}/activity`,
        params: { page, limit }
      }),
      transformResponse: (response: unknown) => normalizePaged<AdminActivityRow>(response),
      providesTags: (_result, _error, arg) => [
        { type: 'PropertyActivity', id: String(arg.propertyId) }
      ]
    }),

    /** Writes a status-history row and an activity-log row, so both feeds go stale with it. */
    updateAdminPropertyStatus: builder.mutation<IdResult, UpdateAdminPropertyStatusArgs>({
      query: ({ id, status, note, override, reason }) => ({
        url: `admin/properties/${id}/status`,
        method: 'PUT',
        body: { status, note, override, reason }
      }),
      transformResponse: (response: unknown) => normalizeIdResult(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AdminProperty', id: 'LIST' },
        { type: 'AdminProperty', id: 'COUNTS' },
        { type: 'AdminProperty', id: String(arg.id) },
        { type: 'PropertyActivity', id: String(arg.id) },
        { type: 'PropertyActivity', id: 'GLOBAL' },
        { type: 'DashboardStats', id: 'ADMIN' },
        { type: 'Notification', id: 'ADMIN' }
      ]
    }),

    /** Moves `submitted`/`under_review` to `lawyer_assigned`, so the list row changes too. */
    assignLawyerToProperty: builder.mutation<IdResult, AssignLawyerArgs>({
      query: ({ propertyId, lawyerId }) => ({
        url: `admin/properties/${propertyId}/assign-lawyer`,
        method: 'POST',
        body: { lawyerId }
      }),
      transformResponse: (response: unknown) => normalizeIdResult(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AdminProperty', id: 'LIST' },
        { type: 'AdminProperty', id: 'COUNTS' },
        { type: 'AdminProperty', id: String(arg.propertyId) },
        { type: 'PropertyActivity', id: String(arg.propertyId) },
        { type: 'PropertyActivity', id: 'GLOBAL' },
        { type: 'AdminLawyer', id: 'LIST' },
        { type: 'AdminLawyer', id: String(arg.lawyerId) },
        { type: 'DashboardStats', id: 'ADMIN' }
      ]
    }),

    reopenAdminPropertyReview: builder.mutation<IdResult, { id: number; reason?: string | null }>({
      query: ({ id, reason }) => ({
        url: `admin/properties/${id}/reopen-review`,
        method: 'POST',
        body: { reason: reason ?? null }
      }),
      transformResponse: (response: unknown) => normalizeIdResult(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AdminProperty', id: 'LIST' },
        { type: 'AdminProperty', id: 'COUNTS' },
        { type: 'AdminProperty', id: String(arg.id) },
        { type: 'PropertyActivity', id: String(arg.id) },
        { type: 'PropertyActivity', id: 'GLOBAL' },
        { type: 'DashboardStats', id: 'ADMIN' }
      ]
    }),

    /* ───────────── property managers ───────────── */

    getPropertyManagers: builder.query<
      Paged<AdminPropertyManagerRow>,
      AdminPropertyManagerQueryArgs | void
    >({
      query: (args) => ({ url: 'admin/property-managers', params: { ...(args ?? {}) } }),
      transformResponse: (response: unknown) => normalizePaged<AdminPropertyManagerRow>(response),
      providesTags: (result) => [
        { type: 'AdminPropertyManager' as const, id: 'LIST' },
        ...(result?.items ?? []).map((row) => ({
          type: 'AdminPropertyManager' as const,
          id: String(row.id)
        }))
      ]
    }),

    createPropertyManager: builder.mutation<IdResult, CreatePropertyManagerArgs>({
      query: (body) => ({ url: 'admin/property-managers', method: 'POST', body }),
      transformResponse: (response: unknown) => normalizeIdResult(response),
      invalidatesTags: [{ type: 'AdminPropertyManager', id: 'LIST' }]
    }),

    updatePropertyManager: builder.mutation<IdResult, UpdatePropertyManagerArgs>({
      query: ({ id, ...body }) => ({ url: `admin/property-managers/${id}`, method: 'PUT', body }),
      transformResponse: (response: unknown) => normalizeIdResult(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AdminPropertyManager', id: 'LIST' },
        { type: 'AdminPropertyManager', id: String(arg.id) }
      ]
    }),

    /** Mirrors `assignLawyerToProperty`'s invalidation: property + activity + own-entity tags. */
    assignManagerToProperty: builder.mutation<IdResult, AssignManagerArgs>({
      query: ({ propertyId, managerId }) => ({
        url: `admin/properties/${propertyId}/assign-manager`,
        method: 'POST',
        body: { managerId }
      }),
      transformResponse: (response: unknown) => normalizeIdResult(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AdminProperty', id: 'LIST' },
        { type: 'AdminProperty', id: String(arg.propertyId) },
        { type: 'PropertyActivity', id: String(arg.propertyId) },
        { type: 'PropertyActivity', id: 'GLOBAL' },
        { type: 'AdminPropertyManager', id: 'LIST' },
        { type: 'AdminPropertyManager', id: String(arg.managerId) },
        { type: 'DashboardStats', id: 'ADMIN' }
      ]
    }),

    /* ───────────── documents ───────────── */

    /**
     * `verify` needs no reason; `correction` and `reject` are rejected without one. The
     * document lives inside the property detail payload, which is why `propertyId` is on the
     * args — it is used for invalidation only and never reaches the request.
     */
    reviewDocument: builder.mutation<IdResult, ReviewDocumentArgs>({
      query: ({ docId, action, reason }) => ({
        url: `admin/documents/${docId}/review`,
        method: 'PUT',
        body: { action, reason }
      }),
      transformResponse: (response: unknown) => normalizeIdResult(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AdminProperty', id: String(arg.propertyId) },
        { type: 'PropertyDocument', id: String(arg.propertyId) },
        { type: 'PropertyDocument', id: String(arg.docId) },
        { type: 'PropertyActivity', id: String(arg.propertyId) },
        { type: 'DashboardStats', id: 'ADMIN' },
        { type: 'Notification', id: 'ADMIN' }
      ]
    }),

    /** The whole re-upload chain from the original submission, oldest version first. */
    getDocumentVersions: builder.query<DocumentVersionRow[], { docId: number }>({
      query: ({ docId }) => ({ url: `admin/documents/${docId}/versions` }),
      transformResponse: (response: unknown) => normalizeRows<DocumentVersionRow>(response),
      providesTags: (_result, _error, arg) => [{ type: 'PropertyDocument', id: String(arg.docId) }]
    }),

    /* ───────────── sellers ───────────── */

    getAdminSellers: builder.query<Paged<AdminSellerListItem>, AdminSellerQueryArgs>({
      query: (params) => ({ url: 'admin/sellers', params: { ...params } }),
      transformResponse: (response: unknown) => normalizePaged<AdminSellerListItem>(response),
      providesTags: (result) => [
        { type: 'AdminSeller' as const, id: 'LIST' },
        ...(result?.items ?? []).map((row) => ({
          type: 'AdminSeller' as const,
          id: String(row.id)
        }))
      ]
    }),

    getAdminSellerDetail: builder.query<AdminSellerDetail, { id: number }>({
      query: ({ id }) => ({ url: `admin/sellers/${id}` }),
      transformResponse: (response: unknown) => normalizeAdminSellerDetail(response),
      providesTags: (_result, _error, arg) => [{ type: 'AdminSeller', id: String(arg.id) }]
    }),

    /* ───────────── lawyers ───────────── */

    getAdminLawyers: builder.query<Paged<AdminLawyerRow>, AdminLawyerQueryArgs | void>({
      query: (args) => ({ url: 'admin/lawyers', params: { ...(args ?? {}) } }),
      transformResponse: (response: unknown) => normalizePaged<AdminLawyerRow>(response),
      providesTags: (result) => [
        { type: 'AdminLawyer' as const, id: 'LIST' },
        ...(result?.items ?? []).map((row) => ({
          type: 'AdminLawyer' as const,
          id: String(row.id)
        }))
      ]
    }),

    /** No password: the lawyer's first login is the existing OTP flow. */
    createLawyer: builder.mutation<CreateLawyerResult, CreateLawyerArgs>({
      query: (body) => ({ url: 'admin/lawyers', method: 'POST', body }),
      transformResponse: (response: unknown) => normalizeCreateLawyerResult(response),
      invalidatesTags: [{ type: 'AdminLawyer', id: 'LIST' }]
    }),

    getAdminLawyerDetail: builder.query<AdminLawyerDetail, { id: number }>({
      query: ({ id }) => ({ url: `admin/lawyers/${id}` }),
      transformResponse: (response: unknown) => normalizeAdminLawyerDetail(response),
      providesTags: (_result, _error, arg) => [{ type: 'AdminLawyer', id: String(arg.id) }]
    }),

    /** Verifying also clears a suspension — one call, two columns. */
    verifyLawyer: builder.mutation<IdResult, { id: number }>({
      query: ({ id }) => ({ url: `admin/lawyers/${id}/verify`, method: 'PUT' }),
      transformResponse: (response: unknown) => normalizeIdResult(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AdminLawyer', id: 'LIST' },
        { type: 'AdminLawyer', id: String(arg.id) }
      ]
    }),

    suspendLawyer: builder.mutation<IdResult, { id: number; reason: string }>({
      query: ({ id, reason }) => ({
        url: `admin/lawyers/${id}/suspend`,
        method: 'PUT',
        body: { reason }
      }),
      transformResponse: (response: unknown) => normalizeIdResult(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AdminLawyer', id: 'LIST' },
        { type: 'AdminLawyer', id: String(arg.id) }
      ]
    }),

    reactivateLawyer: builder.mutation<IdResult, { id: number }>({
      query: ({ id }) => ({ url: `admin/lawyers/${id}/reactivate`, method: 'PUT' }),
      transformResponse: (response: unknown) => normalizeIdResult(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AdminLawyer', id: 'LIST' },
        { type: 'AdminLawyer', id: String(arg.id) }
      ]
    }),

    /* ───────────── legal requests ───────────── */

    getAdminLegalRequests: builder.query<AdminLegalRequestRow[], AdminLegalRequestQueryArgs | void>(
      {
        query: (args) => ({ url: 'admin/legal-requests', params: { ...(args ?? {}) } }),
        transformResponse: (response: unknown) => normalizeItems<AdminLegalRequestRow>(response),
        providesTags: (result) => [
          { type: 'LegalRequest' as const, id: 'LIST' },
          ...(result ?? []).map((row) => ({ type: 'LegalRequest' as const, id: String(row.id) }))
        ]
      }
    ),

    updateLegalRequestStatus: builder.mutation<MessageResult, UpdateLegalRequestStatusArgs>({
      query: ({ id, status, assignedLawyerId, notes }) => ({
        url: `admin/legal-requests/${id}/status`,
        method: 'PUT',
        body: { status, assignedLawyerId, notes }
      }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'LegalRequest', id: 'LIST' },
        { type: 'LegalRequest', id: String(arg.id) }
      ]
    }),

    /* ───────────── buyers ───────────── */

    /** `{ items }` with no paging — the whole matching set, every time. */
    listAdminBuyers: builder.query<AdminBuyerListItem[], AdminBuyerQueryArgs | void>({
      query: (args) => ({ url: 'admin/buyers', params: { ...(args ?? {}) } }),
      transformResponse: (response: unknown) => normalizeItems<AdminBuyerListItem>(response),
      providesTags: (result) => [
        { type: 'AdminBuyer' as const, id: 'LIST' },
        ...(result ?? []).map((row) => ({ type: 'AdminBuyer' as const, id: String(row.id) }))
      ]
    }),

    getAdminBuyer: builder.query<AdminBuyerDetail, { id: number }>({
      query: ({ id }) => ({ url: `admin/buyers/${id}` }),
      transformResponse: (response: unknown) => normalizeAdminBuyerDetail(response),
      providesTags: (_result, _error, arg) => [{ type: 'AdminBuyer', id: String(arg.id) }]
    }),

    /* ───────────── buyer enquiries ───────────── */

    listAdminBuyerEnquiries: builder.query<AdminBuyerEnquiryRow[], void>({
      query: () => ({
        url: 'admin/buyer-enquiries',
        params: { page: 1, limit: ADMIN_BUYER_ENQUIRY_QUEUE_LIMIT }
      }),
      transformResponse: (response: unknown) => normalizeItems<AdminBuyerEnquiryRow>(response),
      providesTags: (result) => [
        { type: 'BuyerEnquiry' as const, id: 'LIST' },
        ...(result ?? []).map((row) => ({ type: 'BuyerEnquiry' as const, id: String(row.id) }))
      ]
    }),

    getAdminBuyerEnquiry: builder.query<AdminBuyerEnquiryDetail, { id: number }>({
      query: ({ id }) => ({ url: `admin/buyer-enquiries/${id}` }),
      transformResponse: (response: unknown) => normalizeAdminBuyerEnquiryDetail(response),
      providesTags: (_result, _error, arg) => [{ type: 'BuyerEnquiry', id: String(arg.id) }]
    }),

    /** An approval both reveals fields and moves the status, so list and detail both go. */
    approveBuyerEnquiry: builder.mutation<MessageResult, ApproveBuyerEnquiryArgs>({
      query: ({ id, sharedFields, noteToBuyer, internalNote }) => ({
        url: `admin/buyer-enquiries/${id}/approve`,
        method: 'PUT',
        body: { sharedFields, noteToBuyer, internalNote }
      }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'BuyerEnquiry', id: 'LIST' },
        { type: 'BuyerEnquiry', id: String(arg.id) },
        { type: 'AdminBuyer', id: 'LIST' }
      ]
    }),

    rejectBuyerEnquiry: builder.mutation<MessageResult, { id: number; reason: string }>({
      query: ({ id, reason }) => ({
        url: `admin/buyer-enquiries/${id}/reject`,
        method: 'PUT',
        body: { reason }
      }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'BuyerEnquiry', id: 'LIST' },
        { type: 'BuyerEnquiry', id: String(arg.id) },
        { type: 'AdminBuyer', id: 'LIST' }
      ]
    }),

    askBuyerEnquiryInfo: builder.mutation<MessageResult, { id: number; question: string }>({
      query: ({ id, question }) => ({
        url: `admin/buyer-enquiries/${id}/ask-info`,
        method: 'PUT',
        body: { question }
      }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'BuyerEnquiry', id: 'LIST' },
        { type: 'BuyerEnquiry', id: String(arg.id) }
      ]
    }),

    /**
     * Sent with NO body, exactly as the axios call did — the broker equivalent below sends
     * `{}`. Neither endpoint reads a body, and the difference is preserved rather than tidied.
     */
    forwardBuyerEnquiryToSeller: builder.mutation<MessageResult, { id: number }>({
      query: ({ id }) => ({
        url: `admin/buyer-enquiries/${id}/forward-seller`,
        method: 'PUT'
      }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'BuyerEnquiry', id: 'LIST' },
        { type: 'BuyerEnquiry', id: String(arg.id) }
      ]
    }),

    /* ───────────── broker enquiries ───────────── */

    getAdminEnquiries: builder.query<
      Paged<AdminEnquiryRow>,
      { page?: number; limit?: number } | void
    >({
      query: (args) => ({ url: 'admin/enquiries', params: { ...(args ?? {}) } }),
      transformResponse: (response: unknown) => normalizePaged<AdminEnquiryRow>(response),
      providesTags: (result) => [
        { type: 'Enquiry' as const, id: 'LIST' },
        ...(result?.items ?? []).map((row) => ({ type: 'Enquiry' as const, id: String(row.id) }))
      ]
    }),

    getAdminEnquiry: builder.query<AdminEnquiryDetail | null, { id: number }>({
      query: ({ id }) => ({ url: `admin/enquiries/${id}` }),
      transformResponse: (response: unknown) => normalizeAdminEnquiry(response),
      providesTags: (_result, _error, arg) => [{ type: 'Enquiry', id: String(arg.id) }]
    }),

    approveAdminEnquiry: builder.mutation<MessageResult, ApproveAdminEnquiryArgs>({
      query: ({ id, sharedFields, noteToBroker, internalNote }) => ({
        url: `admin/enquiries/${id}/approve`,
        method: 'PUT',
        body: { sharedFields, noteToBroker, internalNote }
      }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Enquiry', id: 'LIST' },
        { type: 'Enquiry', id: String(arg.id) }
      ]
    }),

    rejectAdminEnquiry: builder.mutation<MessageResult, { id: number; reason: string }>({
      query: ({ id, reason }) => ({
        url: `admin/enquiries/${id}/reject`,
        method: 'PUT',
        body: { reason }
      }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Enquiry', id: 'LIST' },
        { type: 'Enquiry', id: String(arg.id) }
      ]
    }),

    askAdminEnquiryInfo: builder.mutation<MessageResult, { id: number; question: string }>({
      query: ({ id, question }) => ({
        url: `admin/enquiries/${id}/ask-info`,
        method: 'PUT',
        body: { question }
      }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Enquiry', id: 'LIST' },
        { type: 'Enquiry', id: String(arg.id) }
      ]
    }),

    /** Sends an empty object body — the buyer equivalent above sends none. Preserved. */
    forwardAdminEnquiryToSeller: builder.mutation<MessageResult, { id: number }>({
      query: ({ id }) => ({
        url: `admin/enquiries/${id}/forward-seller`,
        method: 'PUT',
        body: {}
      }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Enquiry', id: 'LIST' },
        { type: 'Enquiry', id: String(arg.id) }
      ]
    }),

    /* ───────────── deals ───────────── */

    getAdminDeals: builder.query<Paged<AdminDealRow>, { page?: number; limit?: number } | void>({
      query: (args) => ({ url: 'admin/deals', params: { ...(args ?? {}) } }),
      transformResponse: (response: unknown) => normalizePaged<AdminDealRow>(response),
      providesTags: (result) => [
        { type: 'Deal' as const, id: 'LIST' },
        ...(result?.items ?? []).map((row) => ({ type: 'Deal' as const, id: String(row.id) }))
      ]
    }),

    getAdminDeal: builder.query<AdminDealDetail, { id: number }>({
      query: ({ id }) => ({ url: `admin/deals/${id}` }),
      transformResponse: (response: unknown) => normalizeAdminDealDetail(response),
      providesTags: (_result, _error, arg) => [
        { type: 'Deal', id: String(arg.id) },
        { type: 'DealNote', id: String(arg.id) }
      ]
    }),

    /** Filed `admin_only`: the broker never sees the admin's assessment of their own deal. */
    createAdminDealNote: builder.mutation<IdResult, { id: number; note: string }>({
      query: ({ id, note }) => ({ url: `admin/deals/${id}/notes`, method: 'POST', body: { note } }),
      transformResponse: (response: unknown) => normalizeIdResult(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Deal', id: String(arg.id) },
        { type: 'DealNote', id: String(arg.id) }
      ]
    }),

    flagAdminDeal: builder.mutation<MessageResult, { id: number; reason: string }>({
      query: ({ id, reason }) => ({
        url: `admin/deals/${id}/flag`,
        method: 'PUT',
        body: { reason }
      }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Deal', id: 'LIST' },
        { type: 'Deal', id: String(arg.id) }
      ]
    })
  })
});

export const {
  useGetAdminStatsQuery,
  useGetAdminActivityQuery,
  useGetAdminNotificationsQuery,
  useGetAdminPropertiesQuery,
  useLazyGetAdminPropertiesQuery,
  useGetAdminPropertyCountsQuery,
  useGetAdminDistrictsQuery,
  useGetAdminPropertyDetailQuery,
  useGetAdminPropertyActivityQuery,
  useUpdateAdminPropertyStatusMutation,
  useReopenAdminPropertyReviewMutation,
  useAssignLawyerToPropertyMutation,
  useGetPropertyManagersQuery,
  useCreatePropertyManagerMutation,
  useUpdatePropertyManagerMutation,
  useAssignManagerToPropertyMutation,
  useReviewDocumentMutation,
  useGetDocumentVersionsQuery,
  useGetAdminSellersQuery,
  useGetAdminSellerDetailQuery,
  useGetAdminLawyersQuery,
  useCreateLawyerMutation,
  useGetAdminLawyerDetailQuery,
  useVerifyLawyerMutation,
  useSuspendLawyerMutation,
  useReactivateLawyerMutation,
  useGetAdminLegalRequestsQuery,
  useUpdateLegalRequestStatusMutation,
  useListAdminBuyersQuery,
  useGetAdminBuyerQuery,
  useListAdminBuyerEnquiriesQuery,
  useGetAdminBuyerEnquiryQuery,
  useApproveBuyerEnquiryMutation,
  useRejectBuyerEnquiryMutation,
  useAskBuyerEnquiryInfoMutation,
  useForwardBuyerEnquiryToSellerMutation,
  useGetAdminEnquiriesQuery,
  useGetAdminEnquiryQuery,
  useApproveAdminEnquiryMutation,
  useRejectAdminEnquiryMutation,
  useAskAdminEnquiryInfoMutation,
  useForwardAdminEnquiryToSellerMutation,
  useGetAdminDealsQuery,
  useGetAdminDealQuery,
  useCreateAdminDealNoteMutation,
  useFlagAdminDealMutation
} = adminApi;
